// src/components/TopicMafia/components/GuessDialog.jsx
import React, { useMemo, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Typography,
    TextField,
    Button,
    Chip,
    Divider,
    Alert,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { mainActionBtnSx, subActionBtnSx } from "../topicMafiaUi";

export default function GuessDialog({
    open,
    onClose,
    preventClose = false,
    guessText,
    setGuessText,
    remainingLabel = "",
    disabledSubmit = false,
    onSubmit,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const helper = useMemo(() => {
        const len = (guessText || "").trim().length;
        if (len === 0) return "상대 팀의 주제를 입력해줘. (예: ‘국내 영화’, ‘과일’, ‘가구’)";
        if (len > 40) return "너무 길어. 핵심만 짧게!";
        return "좋아. 제출하면 되겠어.";
    }, [guessText]);

    const canEnter = !disabledSubmit && (guessText || "").trim().length >= 1;

    // ✅ preventClose 정책 적용
    const handleClose = useCallback(
        (event, reason) => {
            if (preventClose) return; // ✅ 추측자면 어떤 경로로도 닫기 금지
            onClose?.(event, reason);
        },
        [preventClose, onClose]
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            disableEscapeKeyDown={preventClose} // ✅ ESC 차단
            fullScreen={isMobile}
            fullWidth
            maxWidth={isMobile ? false : "sm"}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4 } }}
        >
            <DialogTitle sx={{ fontWeight: 900 }}>🧠 마피아 주제 추측</DialogTitle>

            <DialogContent>
                <Stack spacing={1.5}>
                    <Alert severity="warning" sx={{ borderRadius: 3 }}>
                        제한 시간 안에 <b>상대 팀의 주제</b>를 맞히면 마피아 팀이 승리!
                        {remainingLabel ? (
                            <>
                                <br />
                                ⏳ 남은 시간: <b>{remainingLabel}</b>
                            </>
                        ) : null}
                    </Alert>

                    <Divider />

                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                                size="small"
                                variant="outlined"
                                label={remainingLabel ? `⏳ ${remainingLabel}` : "⏳ ..."}
                                sx={{ fontWeight: 900 }}
                            />
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                띄어쓰기없이 작성해주세요
                            </Typography>
                        </Stack>

                        <TextField
                            autoFocus
                            label="추측 주제"
                            placeholder="예: 과일 / 국내 영화 / 가구 / 동물 / K-POP ..."
                            value={guessText}
                            onChange={(e) => setGuessText(e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 60 }}
                            helperText={helper}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && canEnter) onSubmit?.();
                            }}
                        />
                    </Stack>

                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        * 제출은 1회로 제한 추천 (백엔드에서 1회 제출 후 차단)
                    </Typography>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={
                    isMobile
                        ? { position: "sticky", bottom: 0, bgcolor: "background.paper", zIndex: 1, px: 2, pb: 1.5 }
                        : { px: 3, pb: 2.5 }
                }
            >
                <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1}
                    sx={{ width: "100%" }}
                    justifyContent="flex-end"
                >
                    {/* ✅ 추측자면 닫기 버튼 숨김(또는 disabled로 변경해도 됨) */}
                    {!preventClose && (
                        <Button variant="outlined" onClick={onClose} sx={subActionBtnSx} fullWidth={isMobile}>
                            닫기
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        onClick={onSubmit}
                        disabled={disabledSubmit || (guessText || "").trim().length < 1}
                        sx={mainActionBtnSx}
                        fullWidth={isMobile}
                    >
                        제출
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
